import { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import api from '../../api/client';
import DurationDisplay from '../common/DurationDisplay';

const EVENT_TYPES = [
  { value: 'all', label: 'Todos' },
  { value: 'music', label: 'Música' },
  { value: 'program', label: 'Sets / Programas' },
  { value: 'ad_break', label: 'Tanda' },
  { value: 'jingle', label: 'Jingle' },
  { value: 'promo', label: 'Promo' },
  { value: 'fallback', label: 'Fallback' },
  { value: 'spot', label: 'Spot' },
  { value: 'manual', label: 'Manual' },
];

// Acceso rápido a lo más consultado (los sets de los bloques de rotación = program)
const QUICK_FILTERS = [
  { value: 'all', label: 'Todo' },
  { value: 'music', label: 'Música' },
  { value: 'program', label: 'Sets' },
  { value: 'ad_break', label: 'Tandas' },
];

const TYPE_LABELS = {
  music: 'Música',
  program: 'Set / Programa',
  ad_break: 'Tanda',
  jingle: 'Jingle',
  promo: 'Promo',
  fallback: 'Fallback',
  spot: 'Spot',
  manual: 'Manual',
};

const TRIGGER_LABELS = {
  dj_set: 'set de DJ',
  episode: 'episodio',
  manual: 'manual',
  fallback: 'fallback',
  commercial_break: 'tanda',
  show_intro: 'intro',
  show_separator: 'separador',
  show_closing: 'cierre',
  rotation_filler: 'relleno rotación',
};

export default function BroadcastHistory() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchEvents();
  }, [typeFilter, dateFrom, dateTo]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.broadcast_type = typeFilter;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;

      const { data } = await api.get('/api/history/broadcast', { params });
      setEvents(data.history || []);
    } catch (err) {
      console.error('[BroadcastHistory] Failed to fetch:', err);
    } finally {
      setLoading(false);
    }
  };

  const q = search.trim().toLowerCase();
  const visibleEvents = events.filter((e) => {
    if (!q) return true;
    return (
      (e.title || '').toLowerCase().includes(q) ||
      (e.artist || '').toLowerCase().includes(q) ||
      (e.program_name || '').toLowerCase().includes(q)
    );
  });

  const handleClearFilters = () => {
    setTypeFilter('all');
    setDateFrom('');
    setDateTo('');
    setSearch('');
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">Historial de emisiones</h1>
        <button onClick={fetchEvents} className="btn btn-secondary btn-sm" title="Actualizar">
          ↻
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {QUICK_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setTypeFilter(f.value)}
            className={`rounded-full px-3 py-1 text-xs font-medium transition ${
              typeFilter === f.value
                ? 'bg-primary-600 text-white'
                : 'bg-dark-800 text-dark-300 hover:bg-dark-700'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Buscar tema, artista o programa..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input max-w-sm text-sm"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="input text-sm w-44"
        >
          {EVENT_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="input text-sm w-40"
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="input text-sm w-40"
        />
        {(typeFilter !== 'all' || dateFrom || dateTo || search) && (
          <button onClick={handleClearFilters} className="text-xs btn btn-outline btn-sm">
            Limpiar
          </button>
        )}
      </div>

      {loading ? (
        <div className="py-12 text-center text-dark-400">Cargando historial...</div>
      ) : events.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No hay registros de emisión todavía.
        </div>
      ) : visibleEvents.length === 0 ? (
        <div className="py-12 text-center text-dark-400">
          No se encontraron resultados para la búsqueda.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Tipo</th>
                <th>Tema</th>
                <th>Artista</th>
                <th>Programa</th>
                <th>Duración</th>
              </tr>
            </thead>
            <tbody>
              {visibleEvents.map((event) => (
                <tr key={event.id}>
                  <td className="text-sm text-dark-400">
                    {event.broadcast_at ? format(parseISO(event.broadcast_at), 'PPp') : '—'}
                  </td>
                  <td>
                    <span className="text-xs font-medium px-2 py-0.5 rounded bg-dark-700 text-dark-300 uppercase">
                      {TYPE_LABELS[event.broadcast_type] || event.broadcast_type || '—'}
                    </span>
                    {event.broadcast_type === 'program' && event.trigger_source && (
                      <span className="ml-1 text-[10px] text-dark-500" title={`Origen: ${event.trigger_source}`}>
                        {TRIGGER_LABELS[event.trigger_source] || event.trigger_source}
                      </span>
                    )}
                  </td>
                  <td className="font-medium text-dark-100">
                    {event.media_file_id ? (
                      <Link to={`/media/${event.media_file_id}`} className="hover:text-primary-400" title="Abrir archivo (editar/borrar)">
                        {event.title || '—'}
                      </Link>
                    ) : (
                      event.title || '—'
                    )}
                  </td>
                  <td className="text-sm text-dark-300">{event.artist || '—'}</td>
                  <td className="text-sm text-dark-300">
                    {event.program_name || '—'}
                    {event.media_file_id && (
                      <Link
                        to={`/media/${event.media_file_id}`}
                        className="ml-2 text-xs text-primary-400 hover:text-primary-300"
                        title="Abrir archivo"
                      >
                        ▶ Editar/Borrar
                      </Link>
                    )}
                  </td>
                  <td className="text-sm text-dark-400">
                    {event.duration_seconds ? <DurationDisplay seconds={event.duration_seconds} /> : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
